import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import InputBox from "../common/InputBox";
import SearchSelect from "../common/SearchSelect";

export default function IsobTab1({ formValues, handleInputChange, error, setError, mandatoryFields, setMandatoryFields, getDropDownOptions, getIsobDropDownOptions, readOnly }) {
    const SOLD_TO_FIELDS = [
        { name: "BPCode", label: "BPCode*", maxLength: 20, required: true, validation: "isAlphanumeric" },
        { name: "BPName", label: "BPName*", maxLength: 50, required: true, validation: "isAlphanumeric" },
        { name: "BPGSTN", label: "BPGSTN*", maxLength: 20, required: true, validation: "isAlphanumeric" },
        { name: "CIN", label: "CIN", maxLength: 20, required: false, validation: "isAlphanumeric" },
        { name: "BPTAN", label: "BP TAN", maxLength: 20, required: false, validation: "isAlphanumeric" },
        { name: "Location", label: "Location *", maxLength: 20, required: true, validation: "isAlphanumeric" },
        { name: "Pincode", label: "Pincode *", maxLength: 6, required: true, validation: "isNumeric" },
        { name: "BPAddress", label: "BP Address *", maxLength: 500, required: true, validation: "isAlphanumericWithSpecialChars" },
        { name: "ContactName", label: "Name *", maxLength: 20, required: true, validation: "isAlphaWithSpace" },
        { name: "Mobile", label: "Mobile *", maxLength: 10, required: true, validation: "isMobileNumber" },
        { name: "Email", label: "Email *", maxLength: 50, required: true, validation: "Email" },
        { name: "Designation", label: "Designation *", maxLength: 50, required: true, validation: "isAlphanumeric" },
        { name: "AddressCode", label: "Address Code", maxLength: 20, required: false, validation: "isAlphanumeric" },
    ];

    const OTHER_DETAILS_ROWS = [
        { fields: ["PO_number", "POLOIDate", "DAPTechApprovalDate", "BOM_type"] },
        { fields: ["Segment", "WHType","SKU_stored", "DocumentExchangePlace"] },
        { fields: ["RelatedToPastSupply", "PastSONo"] },
    ];

    const OTHER_DETAILS_FIELDS = {
        PO_number: { label: "PO Number *", type: "text", maxLength: 100, required: true, validation: "isAlphanumericWithSpecialChars" },
        POLOIDate: { label: "PO/LOI Date *", type: "date", required: true },
        DAPTechApprovalDate: { label: "DAP/Tech Approval Date", type: "date", required: false },
        BOM_type: { label: "BOM Type *", type: "select", required: true, dropdownKey: "BOM Type", hint: "Kindly select BOM Type" },
        Segment: { label: "Segment *", type: "select", required: true, dropdownKey: "Segment", hint: "Kindly select Segment" },
        WHType: { label: "W/H Type *", type: "select", required: true, dropdownKey: "WH Type", hint: "Kindly select W/H Type" },
        SKU_stored: { label: "SKU Stored", type: "text", maxLength: 50, required: false, validation: "isAlphanumeric" },
        DocumentExchangePlace: { label: "Document Exchange Place", type: "text", maxLength: 50, required: false, validation: "isAlphanumeric" },
        RelatedToPastSupply: { label: "Related to Past Supply *", type: "radio", required: true, options: ["Yes", "No"] },
        PastSONo: { label: "Past SO No.", type: "text", maxLength: 20, required: false, validation: "isAlphanumeric" },
    };

    const MOBILE_PREVIEW_COUNT = 4;

    const copySoldTo = !!formValues.copySoldTo;

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const [openSections, setOpenSections] = useState({ billing: false, shipping: false, other: false });
    const [showAllBilling, setShowAllBilling] = useState(false);
    const [showAllShipping, setShowAllShipping] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Validate mandatory fields on mount so button is disabled even when accordions are closed
    useEffect(() => {
        if (readOnly) {
            setError({});
            setMandatoryFields([]);
            return;
        }
        const requiredSoldTo = SOLD_TO_FIELDS.filter(f => f.required).map(f => f.name);
        const requiredShipTo = SOLD_TO_FIELDS.filter(f => f.required).map(f => `Ship_${f.name}`);
        const requiredOther = Object.entries(OTHER_DETAILS_FIELDS)
            .filter(([, f]) => f.required)
            .map(([name]) => name);
        const allRequired = [...requiredSoldTo, ...requiredShipTo, ...requiredOther];
        const missing = allRequired.filter(name => !formValues[name]);
        setMandatoryFields(missing);
    }, [formValues]);

    const handleToggleCopy = () => {
        const newVal = !copySoldTo;
        handleInputChange({ target: { name: "copySoldTo", value: newVal } });
        if (newVal) {
            SOLD_TO_FIELDS.forEach((field) => {
                handleInputChange({ target: { name: `Ship_${field.name}`, value: formValues[field.name] || "" } });
            });
        }else {
            SOLD_TO_FIELDS.forEach((field) => {
                handleInputChange({ target: { name: `Ship_${field.name}`, value: "" } });
            });
        }
    };

    const toggleSection = (key) => {
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const renderField = (field, prefix = "") => {
        const fieldName = `${prefix}${field.name}`;
        const validationProp = { [field.validation]: true };
        return (
            <InputBox
                key={fieldName}
                label={field.label}
                name={fieldName}
                id={fieldName}
                type="text"
                value={formValues[fieldName] || ""}
                onChange={handleInputChange}
                error={error?.[fieldName]}
                setError={setError}
                required={!readOnly && field.required}
                mandatoryFields={mandatoryFields}
                setMandatoryFields={setMandatoryFields}
                fieldName={field.label.replace(" *", "").replace("*", "")}
                maxLength={field.maxLength}
                readOnly={readOnly || (prefix === "" && (field.name === "BPCode" || field.name === "BPName")) || (prefix === "Ship_" && copySoldTo)}
                disabled={readOnly || (prefix === "" && (field.name === "BPCode" || field.name === "BPName"))}
                {...validationProp}
            />
        );
    };

    const renderMobileFields = (fields, prefix = "", showAll, setShowAll) => {
        const visibleFields = showAll ? fields : fields.slice(0, MOBILE_PREVIEW_COUNT);
        const remainingCount = fields.length - MOBILE_PREVIEW_COUNT;
        return (
            <div className="isob-tab1-mobile-fields">
                {visibleFields.map((field) => renderField(field, prefix))}
                {!showAll && remainingCount > 0 && (
                    <div className="isob-tab1-view-all" onClick={() => setShowAll(true)}>
                        View all fields ({fields.length}) <span className="isob-tab1-view-all-arrow">&#9662;</span>
                    </div>
                )}
                {showAll && (
                    <div className="isob-tab1-view-all" onClick={() => setShowAll(false)}>
                        Show less <span className="isob-tab1-view-all-arrow open">&#9662;</span>
                    </div>
                )}
            </div>
        );
    };

    const renderOtherDetailsRows = () => {
        return OTHER_DETAILS_ROWS.map((rowConfig, rowIdx) => {
            const { fields: row } = rowConfig;
            const spanCols = row.length < 4;
            return (
                <div className="isob-other-details-row" key={`other-row-${rowIdx}`}>
                    {row.map((name) => {
                        if (name === "PastSONo" && formValues.RelatedToPastSupply !== "Yes") {
                            return null;
                        }
                        const field = OTHER_DETAILS_FIELDS[name];
                        const fieldClass = `isob-other-details-field${spanCols ? " isob-other-details-field-span" : ""}`;
                        if (field.type === "select") {
                            return (
                                <div key={name} className={fieldClass}>
                                    <SearchSelect
                                        name={name}
                                        value={formValues[name]}
                                        // option={getDropDownOptions(field.dropdownKey)}
                                        option={
                                            field.dropdownKey === "BOM Type" ? getDropDownOptions("BOM_Type") :
                                                        field.dropdownKey === "WH Type" ?
                                                            getIsobDropDownOptions("Application") :
                                                            getDropDownOptions(field.dropdownKey)
                                        }
                                        label={field.label}
                                        id={name}
                                        fieldName={field.label.replace(" *", "")}
                                        onChange={handleInputChange}
                                        placeholder=""
                                        required={!readOnly && field.required}
                                        mandatoryFields={mandatoryFields}
                                        setMandatoryFields={setMandatoryFields}
                                        setError={setError}
                                        error={error?.[name]}
                                        hint={field.hint}
                                        readOnly={readOnly}
                                    />
                                </div>
                            );
                        }
                        if (field.type === "radio") {
                            return (
                                <div key={name} className={`${fieldClass} isob-radio-field`}>
                                    <Form.Label className="isob-radio-label">{field.label}</Form.Label>
                                    <div className="isob-radio-options">
                                        {field.options.map((opt) => (
                                            <Form.Check
                                                key={opt}
                                                type="radio"
                                                id={`${name}_${opt}`}
                                                label={opt}
                                                name={name}
                                                value={opt}
                                                checked={formValues[name] === opt}
                                                required={!readOnly && field.required}
                                                mandatoryFields={mandatoryFields}
                                                setMandatoryFields={setMandatoryFields}
                                                // onChange={handleInputChange}
                                                onChange={(e) => {
                                                    handleInputChange(e);
                                                    if (field.required && e.target.value) {
                                                        setMandatoryFields(prev => prev.filter(fieldName => fieldName !== name));
                                                    }
                                                    // Clear Past SO No. when "No" is selected (since field will be hidden)
                                                    if (name === "RelatedToPastSupply" && e.target.value === "No") {
                                                        handleInputChange({ target: { name: "PastSONo", value: "" } });
                                                    }
                                                }}
                                                inline
                                                disabled={readOnly}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        const validationProp = field.validation ? { [field.validation]: true } : {};
                        return (
                            <div key={name} className={fieldClass}>
                                <InputBox
                                    label={field.label}
                                    name={name}
                                    id={name}
                                    type={field.type}
                                    value={formValues[name] || ""}
                                    onChange={handleInputChange}
                                    error={error?.[name]}
                                    setError={setError}
                                    required={!readOnly && field.required}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName={field.label.replace(" *", "")}
                                    maxLength={field.maxLength}
                                    readOnly={readOnly}
                                    disabled={readOnly}
                                    {...validationProp}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        });
    };

    const renderMobileOtherDetails = () => {
        const allFields = OTHER_DETAILS_ROWS.flatMap(r => r.fields);
        return allFields.map((name) => {
            if (name === "PastSONo" && formValues.RelatedToPastSupply !== "Yes") {
                return null;
            }
            const field = OTHER_DETAILS_FIELDS[name];
            if (field.type === "select") {
                return (
                    <div key={name} className="isob-tab1-mobile-field-item">
                        <SearchSelect
                            name={name}
                            value={formValues[name]}
                            option={
                                field.dropdownKey === "BOM Type" ? getDropDownOptions("BOM_Type") :
                                            field.dropdownKey === "WH Type" ? getIsobDropDownOptions("Application") :
                                                getDropDownOptions(field.dropdownKey)
                            }
                            label={field.label}
                            id={name}
                            fieldName={field.label.replace(" *", "")}
                            onChange={handleInputChange}
                            placeholder=""
                            required={!readOnly && field.required}
                            mandatoryFields={mandatoryFields}
                            setMandatoryFields={setMandatoryFields}
                            setError={setError}
                            error={error?.[name]}
                            hint={field.hint}
                        />
                    </div>
                );
            }
            if (field.type === "radio") {
                return (
                    <div key={name} className="isob-tab1-mobile-field-item isob-radio-field">
                        <Form.Label className="isob-radio-label">{field.label}</Form.Label>
                        <div className="isob-radio-options">
                            {field.options.map((opt) => (
                                <Form.Check
                                    key={opt}
                                    type="radio"
                                    id={`${name}_${opt}`}
                                    label={opt}
                                    name={name}
                                    value={opt}
                                    checked={formValues[name] === opt}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        if (field.required && e.target.value) {
                                            setMandatoryFields(prev => prev.filter(fieldName => fieldName !== name));
                                        }
                                        if (name === "RelatedToPastSupply" && e.target.value === "No") {
                                            handleInputChange({ target: { name: "PastSONo", value: "" } });
                                        }
                                    }}
                                    inline
                                />
                            ))}
                        </div>
                    </div>
                );
            }
            const validationProp = field.validation ? { [field.validation]: true } : {};
            return (
                <div key={name} className="isob-tab1-mobile-field-item">
                    <InputBox
                        label={field.label}
                        name={name}
                        id={name}
                        type={field.type}
                        value={formValues[name] || ""}
                        onChange={handleInputChange}
                        error={error?.[name]}
                        setError={setError}
                        required={!readOnly && field.required}
                        mandatoryFields={mandatoryFields}
                        setMandatoryFields={setMandatoryFields}
                        fieldName={field.label.replace(" *", "")}
                        maxLength={field.maxLength}
                        {...validationProp}
                    />
                </div>
            );
        });
    };

    // Mobile View
    if (isMobile) {
        return (
            <div className="form-right-adjust isob-tab1-mobile">
                {/* Billing Details Accordion */}
                <div className="isob-tab1-accordion">
                    <div className="isob-tab1-accordion-header" onClick={() => toggleSection('billing')}>
                        <span className="isob-tab1-accordion-title">Billing Details</span>
                        <span className={`isob-tab1-accordion-arrow ${openSections.billing ? 'open' : ''}`}>&#9662;</span>
                    </div>
                    {openSections.billing && (
                        <div className="isob-tab1-accordion-body">
                            {renderMobileFields(SOLD_TO_FIELDS, "", showAllBilling, setShowAllBilling)}
                        </div>
                    )}
                </div>

                {/* Shipping Details Accordion */}
                <div className="isob-tab1-accordion">
                    <div className="isob-tab1-accordion-header" onClick={() => toggleSection('shipping')}>
                        <span className="isob-tab1-accordion-title">Shipping Details</span>
                        <span className={`isob-tab1-accordion-arrow ${openSections.shipping ? 'open' : ''}`}>&#9662;</span>
                    </div>
                    {openSections.shipping && (
                        <div className="isob-tab1-accordion-body">
                            <div className="copy-toggle-container" style={{ marginBottom: "12px" }}>
                                <Form.Check
                                    type="switch"
                                    id="copySoldToToggle"
                                    label="Same as Billing Details"
                                    checked={copySoldTo}
                                    onChange={handleToggleCopy}
                                />
                            </div>
                            {renderMobileFields(SOLD_TO_FIELDS, "Ship_", showAllShipping, setShowAllShipping)}
                        </div>
                    )}
                </div>

                {/* Other Details Accordion */}
                <div className="isob-tab1-accordion">
                    <div className="isob-tab1-accordion-header" onClick={() => toggleSection('other')}>
                        <span className="isob-tab1-accordion-title">Other Details</span>
                        <span className={`isob-tab1-accordion-arrow ${openSections.other ? 'open' : ''}`}>&#9662;</span>
                    </div>
                    {openSections.other && (
                        <div className="isob-tab1-accordion-body">
                            {renderMobileOtherDetails()}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Desktop View (unchanged)
    return (
        <div className="form-right-adjust">
            {/* Sold To Section */}
            <div className="order-info-section-colored add-edit-form">
                <h6 className="section-subheading">Billing Details</h6>

                <div style={{ display: "flex", gap: "20px", width: "99.5%" }}>
                    {["BPCode", "BPName", "BPGSTN", "CIN", "BPTAN"].map((name) => {
                        const field = SOLD_TO_FIELDS.find(f => f.name === name);
                        const validationProp = { [field.validation]: true };
                        return (
                            <div key={name} style={{ flex: "1 1 0", minWidth: 0 }}>
                                <InputBox
                                    label={field.label}
                                    name={name}
                                    id={name}
                                    type="text"
                                    value={formValues[name] || ""}
                                    onChange={handleInputChange}
                                    error={error?.[name]}
                                    setError={setError}
                                    required={!readOnly && field.required}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName={field.label.replace(" *", "").replace("*", "")}
                                    maxLength={field.maxLength}
                                    readOnly={readOnly || name === "BPCode" || name === "BPName"}
                                    disabled={readOnly || name === "BPCode" || name === "BPName"}
                                    {...validationProp}
                                />
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: "flex", gap: "20px", width: "100%" }}>
                    <div className="isob-loc-pin" style={{ display: "flex", gap: "20px", width: "42%" }}>

                        {["Location", "Pincode"].map((name) => {
                            const field = SOLD_TO_FIELDS.find(f => f.name === name);
                            const validationProp = { [field.validation]: true };
                            return (
                                <div key={name} style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <InputBox
                                        label={field.label}
                                        name={name}
                                        id={name}
                                        type="text"
                                        value={formValues[name] || ""}
                                        onChange={handleInputChange}
                                        error={error?.[name]}
                                        setError={setError}
                                        required={!readOnly && field.required}
                                        mandatoryFields={mandatoryFields}
                                        setMandatoryFields={setMandatoryFields}
                                        fieldName={field.label.replace(" *", "")}
                                        maxLength={field.maxLength}
                                        readOnly={readOnly}
                                        {...validationProp}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="isob-address" style={{ width: "64%" }}>
                        {["BPAddress"].map((name) => {
                            const field = SOLD_TO_FIELDS.find(f => f.name === name);
                            const validationProp = { [field.validation]: true };
                            return (
                                <div key={name} style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <InputBox
                                        label={field.label}
                                        name={name}
                                        id={name}
                                        type="text"
                                        as="textarea"
                                        style={{
                                        "max-width": '98%',
                                        "min-width": '50%',
                                        "width": '-webkit-fill-available',
                                        "min-height": "30px"
                                        }}
                                        value={formValues[name] || ""}
                                        onChange={handleInputChange}
                                        error={error?.[name]}
                                        setError={setError}
                                        required={!readOnly && field.required}
                                        mandatoryFields={mandatoryFields}
                                        setMandatoryFields={setMandatoryFields}
                                        fieldName={field.label.replace(" *", "")}
                                        maxLength={field.maxLength}
                                        readOnly={readOnly}
                                        {...validationProp}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <h6 className="section-subheading">Contact Details</h6>
                <div style={{ display: "flex", gap: "20px", width: "99.5%" }}>
                    {["ContactName", "Mobile", "Email", "Designation", "AddressCode"].map((name) => {
                        const field = SOLD_TO_FIELDS.find(f => f.name === name);
                        const validationProp = { [field.validation]: true };
                        return (
                            <div key={name} style={{ flex: "1 1 0", minWidth: 0 }}>
                                <InputBox
                                    label={field.label}
                                    name={name}
                                    id={name}
                                    type="text"
                                    value={formValues[name] || ""}
                                    onChange={handleInputChange}
                                    error={error?.[name]}
                                    setError={setError}
                                    required={!readOnly && field.required}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName={field.label.replace(" *", "")}
                                    maxLength={field.maxLength}
                                    readOnly={readOnly}
                                    {...validationProp}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="order-info-section-colored add-edit-form">
                <h6 className="section-subheading">Shipping Details</h6>
                {/* Ship To Toggle */}
                <div className="copy-toggle-container" style={{ margin: "15px 0" }}>
                    <Form.Check
                        type="switch"
                        id="copySoldToToggle"
                        label="Same as Billing Details"
                        checked={copySoldTo}
                        onChange={handleToggleCopy}
                        disabled={readOnly}
                    />
                </div>

                <div style={{ display: "flex", gap: "20px", width: "99.5%" }}>
                    {["BPCode", "BPName", "BPGSTN", "CIN", "BPTAN"].map((name) => {
                        const field = SOLD_TO_FIELDS.find(f => f.name === name);
                        const validationProp = { [field.validation]: true };
                        return (
                            <div key={`Ship_${name}`} style={{ flex: "1 1 0", minWidth: 0 }}>
                                <InputBox
                                    label={field.label}
                                    name={`Ship_${name}`}
                                    id={`Ship_${name}`}
                                    type="text"
                                    value={formValues[`Ship_${name}`] || ""}
                                    onChange={handleInputChange}
                                    error={error?.[`Ship_${name}`]}
                                    setError={setError}
                                    required={!readOnly && field.required}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName={field.label.replace(" *", "").replace("*", "")}
                                    maxLength={field.maxLength}
                                    readOnly={readOnly || copySoldTo}
                                    disabled={readOnly}
                                    {...validationProp}
                                />
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: "flex", gap: "20px", width: "100%" }}>
                    <div className="isob-loc-pin" style={{ display: "flex", gap: "20px", width: "42%" }}>
                        {["Location", "Pincode"].map((name) => {
                            const field = SOLD_TO_FIELDS.find(f => f.name === name);
                            const validationProp = { [field.validation]: true };
                            return (
                                <div key={`Ship_${name}`} style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <InputBox
                                        label={field.label}
                                        name={`Ship_${name}`}
                                        id={`Ship_${name}`}
                                        type="text"
                                        value={formValues[`Ship_${name}`] || ""}
                                        onChange={handleInputChange}
                                        error={error?.[`Ship_${name}`]}
                                        setError={setError}
                                        required={!readOnly && field.required}
                                        mandatoryFields={mandatoryFields}
                                        setMandatoryFields={setMandatoryFields}
                                        fieldName={field.label.replace(" *", "")}
                                        maxLength={field.maxLength}
                                        readOnly={readOnly || copySoldTo}
                                        {...validationProp}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="isob-address" style={{ width: "64%" }}>
                        {["BPAddress"].map((name) => {
                            const field = SOLD_TO_FIELDS.find(f => f.name === name);
                            const validationProp = { [field.validation]: true };
                            return (
                                <div key={`Ship_${name}`} style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <InputBox
                                        label={field.label}
                                        name={`Ship_${name}`}
                                        id={`Ship_${name}`}
                                         type="text"
                                        as="textarea"
                                        style={{
                                        "max-width": '98%',
                                        "min-width": '50%',
                                        "width": '-webkit-fill-available',
                                        "min-height": "30px"
                                        }}
                                        value={formValues[`Ship_${name}`] || ""}
                                        onChange={handleInputChange}
                                        error={error?.[`Ship_${name}`]}
                                        setError={setError}
                                        required={!readOnly && field.required}
                                        mandatoryFields={mandatoryFields}
                                        setMandatoryFields={setMandatoryFields}
                                        fieldName={field.label.replace(" *", "")}
                                        maxLength={field.maxLength}
                                        readOnly={readOnly || copySoldTo}
                                        {...validationProp}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <h6 className="section-subheading">Contact Details</h6>
                <div style={{ display: "flex", gap: "20px", width: "99.5%" }}>
                    {["ContactName", "Mobile", "Email", "Designation", "AddressCode"].map((name) => {
                        const field = SOLD_TO_FIELDS.find(f => f.name === name);
                        const validationProp = { [field.validation]: true };
                        return (
                            <div key={`Ship_${name}`} style={{ flex: "1 1 0", minWidth: 0 }}>
                                <InputBox
                                    label={field.label}
                                    name={`Ship_${name}`}
                                    id={`Ship_${name}`}
                                    type="text"
                                    value={formValues[`Ship_${name}`] || ""}
                                    onChange={handleInputChange}
                                    error={error?.[`Ship_${name}`]}
                                    setError={setError}
                                    required={!readOnly && field.required}
                                    mandatoryFields={mandatoryFields}
                                    setMandatoryFields={setMandatoryFields}
                                    fieldName={field.label.replace(" *", "")}
                                    maxLength={field.maxLength}
                                    readOnly={readOnly || copySoldTo}
                                    {...validationProp}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            {/* Other Details Section */}
            <div className="order-info-section-colored add-edit-form">
                <h6 className="section-subheading">Other Details</h6>
                {renderOtherDetailsRows()}
            </div>
        </div>
    );
}
